import { useMemo, useState } from 'react';
import { ChevronDown, Globe } from 'lucide-react';
import { useUniverses } from '@biasafe.queries/securities-universes-data/securities-universes-data';
import {
  useUniverseSecurityItems,
} from '@biasafe.queries/securities-universes-security-items/securities-universes-security-items';
import {
  formatScopeLabel,
  marketOf,
  MARKET_ORDER,
} from '../../config/constants/pointInTimeData';
import {
  STATIC_COUNTRY_COUNTS,
  STATIC_UNIVERSES,
} from '../../config/constants/staticUniverses';
import { FilterCombobox } from '../FilterCombobox';
import { IndexPickerRow } from './IndexPickerRow';
import { computeUniverseStats } from './computeUniverseStats';
import type { Universe } from '@biasafe.models';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import {
  universeCatalogueQuery,
  universeSecuritiesQuery,
} from '@/shared/config/queryDefaults';
import { useUniverseAsOfDate } from '../../../RebalancingSchedule/hooks/useLatestReconstitutionDate';

interface UniverseSelectorProps {
  selectedUniverse: Universe;
  onSelect: (universeId: string) => void;
}

// Display the official Morningstar index name (ends with "Index"); fall back to
// the universe's short name if the API didn't supply one.
const officialNameFor = (u: Universe): string => u.officialName || u.name;

export function UniverseSelector({
  selectedUniverse,
  onSelect,
}: UniverseSelectorProps) {
  // Static index list when the catalog API is down, so the user can still pick
  // a universe and build the config (only the per-index numbers come from CH).
  // Step 2's latest reconstitution date. The universe is resolved AT that
  // date so the eligible set and the portfolio it will rebalance are counted
  // at the same instant; it is also part of the queryKey, so changing the
  // schedule refetches instead of serving the previous date from cache.
  const asOf = useUniverseAsOfDate();
  const { data: chUniverses } = useUniverses(asOf ? { as_of: asOf } : undefined, {
    query: universeCatalogueQuery(asOf),
  });
  const universes = chUniverses?.length ? chUniverses : STATIC_UNIVERSES;
  const { data: allSecurities } = useUniverseSecurityItems(asOf ? { as_of: asOf } : undefined, {
    query: {
      ...universeSecuritiesQuery(asOf),
    },
  });
  const [open, setOpen] = useState(false);
  const [providerFilter, setProviderFilter] = useState('All');
  const [marketFilter, setMarketFilter] = useState('All');

  // Filter options + the filtered list (provider always Morningstar today, but
  // the picker mirrors the Figma's Provider/Market combobox filters).
  const providerOptions = useMemo(() => {
    const set = new Set<string>();
    for (const u of universes as Array<Universe>) {
      if (u.dataProvider) set.add(u.dataProvider);
    }
    return [...set];
  }, [universes]);

  const marketOptions = useMemo(() => {
    const set = new Set(
      (universes as Array<Universe>).map((u) => marketOf(u.coverageLabel)),
    );
    return MARKET_ORDER.filter((m) => set.has(m));
  }, [universes]);

  const filteredUniverses = useMemo(
    () =>
      (universes as Array<Universe>).filter(
        (u) =>
          (providerFilter === 'All' || u.dataProvider === providerFilter) &&
          (marketFilter === 'All' || marketOf(u.coverageLabel) === marketFilter),
      ),
    [universes, providerFilter, marketFilter],
  );

  /** Per-universe stats: countries count & constituents count (shared with
   *  the benchmark picker via computeUniverseStats). */
  const universeStats = useMemo(
    () => computeUniverseStats(allSecurities),
    [allSecurities],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {/*
       * Trigger button mirrors Figma's ghost-variant 2px-bordered combobox:
       *   h-10 px-3 bg-background border-2 border-border, with the hover
       *   state changing only the border color (not the background) so the
       *   surface stays calm against the surrounding bg-primary/[0.02]
       *   universe card. mb-3 gives breathing room between the trigger and
       *   the stats row that follows.
       */}
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          role="combobox"
          aria-expanded={open}
          className="w-full h-10 px-3 justify-between rounded-md bg-background border-2 border-border hover:bg-background hover:border-primary/20 text-sm font-normal text-foreground focus-visible:ring-0 focus-visible:border-primary/50 mb-3"
        >
          <div className="flex items-center gap-3 min-w-0">
            <Globe className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            <span className="truncate font-medium text-[15px]">
              {officialNameFor(selectedUniverse)}
            </span>
          </div>
          <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        <Command>
          <CommandInput placeholder="Search universes..." />
          <CommandList>
            {/* Sticky filter bar — Provider + Market combobox filters
                (Figma UniverseSelection.tsx:1649). */}
            <div className="sticky top-0 z-10 flex items-center gap-4 px-3 py-2 border-b border-border/50 bg-background/70 backdrop-blur-lg">
              <FilterCombobox
                allLabel="All providers"
                value={providerFilter}
                options={providerOptions}
                onChange={setProviderFilter}
              />

              <FilterCombobox
                allLabel="All markets"
                value={marketFilter}
                options={marketOptions}
                onChange={setMarketFilter}
              />
            </div>

            <CommandEmpty>No universe found.</CommandEmpty>
            {filteredUniverses.map((universe: Universe) => {
              const isSelected = selectedUniverse.id === universe.id;
              const stats = universeStats.get(universe.id);
              const countries =
                stats?.countries ?? STATIC_COUNTRY_COUNTS[universe.id];
              return (
                // One purple selected-vs-hover language across every dropdown
                // (Select, ScrollSafeSelect, these comboboxes): the highlighted
                // row (keyboard/mouse) gets the fuller `accent` (violet-100),
                // the currently-selected value a faint `accent/50` + medium
                // weight, in addition to the check.
                <CommandItem
                  key={universe.id}
                  value={officialNameFor(universe)}
                  onSelect={() => {
                    onSelect(universe.id);
                    setOpen(false);
                  }}
                  className={cn(
                    'py-3 px-3 cursor-pointer border-b border-border/50 last:border-0 rounded-none data-[selected=true]:bg-accent hover:bg-accent',
                    isSelected && 'bg-accent/50 font-medium',
                  )}
                >
                  {/* `scope` buckets each CONSTITUENT into only {US, Developed
                      ex-US, Emerging}, so it can't express "Canada" or ex-NA —
                      the index's own coverageLabel is the source of truth. */}
                  <IndexPickerRow
                    name={officialNameFor(universe)}
                    coverageLabel={
                      universe.coverageLabel || formatScopeLabel(universe.scope)
                    }
                    countries={countries}
                    constituents={stats?.constituents}
                    provider={universe.dataProvider}
                    selected={isSelected}
                  />
                </CommandItem>
              );
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
